import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { HeaderPortal, useSetHeaderRecordName } from "../../components/ui-components/HeaderSlot";
import { primaryButtonCls, secondaryButtonCls } from "../../components/ui-components/buttonStyles";
import { scopeToSearchParams } from "../cost-analyzer/useAnalyzerScope";
import { dimensionById } from "../cost-analyzer/analyzerDimensions";
import { useInvestigationQuery, useRerunMutation, usePatchMutation } from "./hooks";
import {
  deriveTitle, invNumber, netDelta, deriveMateriality, unresolvedOwnership,
  primaryActionForStatus,
} from "./format";
import CaseSummaryStrip from "./components/CaseSummaryStrip";
import GovernanceBadges from "./components/GovernanceBadges";
import InvestigationDetailsPanel from "./components/InvestigationDetailsPanel";
import OwnerAssignMenu from "./components/OwnerAssignMenu";
import OverviewTab from "./tabs/OverviewTab";
import EvidenceTab from "./tabs/EvidenceTab";
import DriversTab from "./tabs/DriversTab";
import OwnershipTab from "./tabs/OwnershipTab";
import ImpactTab from "./tabs/ImpactTab";
import ResolutionTab from "./tabs/ResolutionTab";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "evidence", label: "Evidence" },
  { id: "drivers", label: "Drivers" },
  { id: "ownership", label: "Ownership" },
  { id: "impact", label: "Impact" },
  { id: "resolution", label: "Resolution" },
];

export default function InvestigationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const { data: inv, isLoading, error } = useInvestigationQuery(id);
  const rerun = useRerunMutation(id);
  const patch = usePatchMutation(id);
  const [menuOpen, setMenuOpen] = useState(false);

  const title = inv ? deriveTitle(inv, inv?.latestVersion) : null;
  useSetHeaderRecordName(title);

  if (isLoading) return <div className="h-full bg-background-grey p-8 text-text-3">Loading…</div>;
  if (error || !inv) return <div className="h-full bg-background-grey p-8 text-text-3">Investigation not found.</div>;

  const version = inv.latestVersion;
  const drivers = version?.drivers ?? [];
  const ownership = version?.allocation ?? [];
  const closed = inv.status === "closed";
  const net = netDelta(drivers);
  const materiality = inv.materiality || deriveMateriality(net, version?.impact?.monthlyRunRateDelta);
  const unresolved = unresolvedOwnership(ownership);
  const primary = primaryActionForStatus(inv.status);
  const periodRange = fmtRange(inv?.scope?.period);
  const wide = tab === "drivers";

  return (
    <div className="h-full flex flex-col bg-background-grey relative">
      <HeaderPortal slot="title">
        <div className="min-w-0">
          <div className="truncate text-[20px] font-extrabold leading-[1.3] tracking-[-0.015em] text-text font-['Plus_Jakarta_Sans',sans-serif]">{title}</div>
          <div className="truncate text-[12px] text-text-3">Cloud &amp; AI spend · {periodRange || "period"} · {invNumber(inv)}</div>
        </div>
      </HeaderPortal>

      <HeaderPortal slot="tabstrip">
        {TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button key={t.id} type="button" onClick={() => setTab(t.id)} aria-current={active ? "page" : undefined} style={{ height: "100%" }}
              className={`relative box-border inline-flex items-center border-none bg-transparent px-4 text-[13.5px] whitespace-nowrap transition-colors cursor-pointer focus-visible:outline-none ${active ? "text-text font-semibold after:content-[''] after:absolute after:left-0 after:right-0 after:bottom-0 after:h-[3px] after:rounded-t after:bg-primary" : "text-text-3 font-medium hover:text-text-2"}`}>
              {t.label}
            </button>
          );
        })}
      </HeaderPortal>

      <HeaderPortal slot="row2-right">
        <GovernanceBadges status={inv.status} materiality={materiality} confidence={inv.confidenceLabel} />
      </HeaderPortal>

      <HeaderPortal>
        <div className="flex items-center gap-3">
          <OwnerAssignMenu id={inv.id} ownerName={inv.ownerName} disabled={closed} />
          <button type="button" className={secondaryButtonCls} onClick={() => {
            const params = scopeToSearchParams(investigationToAnalyzerParams(inv));
            navigate(`/cost-analyzer?${new URLSearchParams(params).toString()}`);
          }}>Open analysis</button>
          <button type="button" className={primaryButtonCls} disabled={patch.isLoading} onClick={() => patch.mutate({ status: primary.next })}>{primary.label}</button>
          <div className="relative">
            <button type="button" aria-label="More actions" className={secondaryButtonCls} onClick={() => setMenuOpen((o) => !o)}>⋯</button>
            {menuOpen && (
              <div className="absolute right-0 z-20 mt-1 w-40 rounded-card border border-card-bd bg-card py-1 shadow-sm" style={{ width: 160 }}>
                <button type="button" disabled={closed || rerun.isLoading} className="block w-full px-3 py-1.5 text-left text-[13px] text-text-2 hover:bg-surface-2 disabled:opacity-50" onClick={() => { rerun.mutate(); setMenuOpen(false); }}>Re-run</button>
              </div>
            )}
          </div>
        </div>
      </HeaderPortal>

      <div className="flex-1 overflow-auto">
        <div className={`mx-auto ${wide ? "max-w-screen-2xl" : "max-w-screen-xl"} space-y-5 p-8`}>
          {tab === "overview" && <><CaseSummaryStrip inv={inv} version={version} ownership={ownership} /><OverviewTab inv={inv} version={version} drivers={drivers} ownership={ownership} unresolved={unresolved} onGoTo={setTab} /><InvestigationDetailsPanel inv={inv} version={version} unresolved={unresolved} /></>}
          {tab === "evidence" && <EvidenceTab inv={inv} version={version} drivers={drivers} audit={inv.audit} />}
          {tab === "drivers" && <DriversTab drivers={drivers} />}
          {tab === "ownership" && <OwnershipTab ownership={ownership} unresolved={unresolved} />}
          {tab === "impact" && <ImpactTab version={version} />}
          {tab === "resolution" && <ResolutionTab id={inv.id} summary={inv.summary} version={version} ownership={ownership} closed={closed} />}
        </div>
      </div>
    </div>
  );
}

export function investigationToAnalyzerParams(inv) {
  const seed = inv?.scope || {};
  const period = seed.period || {};
  const scope = seed.scope || {};
  const driverDim = seed.driverDimension;
  const dim = driverDim && dimensionById(driverDim)?.available ? driverDim : undefined;
  const budget = scope.kind === "budget" ? String(scope.ref ?? scope.budgetId ?? "") : "";
  return { from: toDateOnly(period.from), to: toDateOnly(period.to), dim, budget, filters: [] };
}

function toDateOnly(v) { return v ? String(v).slice(0, 10) : undefined; }

function fmtRange(w) {
  if (!w?.from || !w?.to) return null;
  const opts = { day: "numeric", month: "short", year: "numeric" };
  return `${new Date(w.from).toLocaleDateString(undefined, opts)} – ${new Date(w.to).toLocaleDateString(undefined, opts)}`;
}
